import { useMemo } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { formattedCentsToDollars, getLocalizedPricing } from '../lib/util/money';
import { FREE_SHIPPING_CODE, FREE_TRIAL_SHIPPING_CODE } from '../types/Product';
import useShippingOptions from './useShippingOptions';

interface FreeShippingDisplayResult {
  /** true if free ground shipping is available (standard cart), false if paid shipping (free trial cart) */
  isFreeShipping: boolean;
  /** The localized shipping price (e.g., "$8.99" or "$12") when not free, null when free */
  shippingPrice: string | null;
}

/**
 * Returns whether to show free shipping or paid shipping text, along with the price when applicable.
 *
 * For regular carts: FREE_SHIPPING_CODE is available -> isFreeShipping = true
 * For free trial carts: FREE_TRIAL_SHIPPING_CODE is available -> isFreeShipping = false, returns price
 */
export default function useFreeShippingDisplay(): FreeShippingDisplayResult {
  const { currency } = useLocale();
  const { shippingOptions } = useShippingOptions();

  return useMemo(() => {
    const freeTrialShipping = shippingOptions.find((option) => option.code === FREE_TRIAL_SHIPPING_CODE);
    const freeShipping = shippingOptions.find((option) => option.code === FREE_SHIPPING_CODE);

    // If free trial shipping is in the available options, this is a free trial cart
    if (freeTrialShipping) {
      const priceInCents = getLocalizedPricing(freeTrialShipping.pricingInCents, currency);
      return {
        isFreeShipping: false,
        shippingPrice: formattedCentsToDollars(priceInCents),
      };
    }

    // If free ground shipping is available, shipping is free
    if (freeShipping) {
      return {
        isFreeShipping: true,
        shippingPrice: null,
      };
    }

    // Default to free shipping if neither option is found (shouldn't happen in practice)
    return {
      isFreeShipping: true,
      shippingPrice: null,
    };
  }, [shippingOptions, currency]);
}
